import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Loader2, AlertCircle, CheckCircle2, XCircle, Calendar,
  RefreshCw, Send, Languages, Plus,
} from 'lucide-react';
import BackButton from '../components/BackButton';
import SEO from '../components/SEO';
import PurchaseModal from '../components/PurchaseModal';
import { useAuth } from '../context/AuthContext';
import { getSubscriptions, cancelSubscription, getSubscriptionPlanList } from '../services/api';
import { formatPrice, normalizePlan, SERVICE_DISCLAIMER } from '../data/plans';
import './SubscriptionPage.css';

// BE durumu "Active" / "Canceled" biçiminde döndürüyor; büyük harfe çevirip eşliyoruz
const STATUS = {
  ACTIVE: { label: 'Aktif', color: '#00C9A7', icon: CheckCircle2 },
  PENDING: { label: 'Ödeme bekleniyor', color: '#D97706', icon: Loader2 },
  UNPAID: { label: 'Ödeme alınamadı', color: '#DC2626', icon: AlertCircle },
  CANCELED: { label: 'İptal edildi', color: '#6B7280', icon: XCircle },
  CANCELLED: { label: 'İptal edildi', color: '#6B7280', icon: XCircle },
  EXPIRED: { label: 'Süresi doldu', color: '#6B7280', icon: XCircle },
  PASSIVE: { label: 'Pasif', color: '#6B7280', icon: XCircle },
};

// Satılan ürünler — abonelik `category` alanıyla eşleşir
// NOT: satılan hizmet bir "İşe Yerleştirme Garantisi" veya iş bulma/aracılık
// hizmeti değil, Veri Analiz ve Form Taslak Hazırlama Yazılımı lisansıdır
// (SaaS) — bkz. disclaimer.
const PRODUCTS = [
  {
    key: 'AutoApply',
    name: 'AI Asistan Lisansı',
    icon: Send,
    color: '#2F3A8F',
    desc: 'Veri Analiz ve Form Taslak Hazırlama Yazılımı: profiline uyan ilanları tarar, verilerini eşleştirir ve form taslağını hazırlar; gönderim yalnızca senin onayınla olur.',
    disclaimer: SERVICE_DISCLAIMER,
    purchaseTitle: 'AI Asistan Lisansını Seç',
  },
  {
    key: 'TransMeet',
    name: 'TransMeet',
    icon: Languages,
    color: '#8B5CF6',
    desc: 'Toplantılarını anlık çevir, kaydet ve özetle.',
    purchaseTitle: 'TransMeet Paketini Seç',
  },
];

const statusKey = (value) => String(value || '').toUpperCase();

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const SubscriptionPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [plansById, setPlansById] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [loadDetail, setLoadDetail] = useState(null);

  const [confirmSub, setConfirmSub] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelDetail, setCancelDetail] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);

  const load = useCallback(() => {
    if (!user?.candidateId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setLoadDetail(null);
    getSubscriptions(user.candidateId)
      .then((list) => setSubs(list))
      .catch((e) => {
        setLoadError(e.code === 'NOT_IMPLEMENTED' || e.code === 'UNAUTHORIZED' ? 'pending' : 'failed');
        setLoadDetail(e.message || null);
        console.error('[Subscription] GetSubscription başarısız:', e);
      })
      .finally(() => setLoading(false));
  }, [user?.candidateId]);

  useEffect(() => { load(); }, [load]);

  // Paket adları BE'de İngilizce; listedeki Türkçe karşılığı kullanılır
  useEffect(() => {
    getSubscriptionPlanList()
      .then((list) => {
        const map = {};
        (list || []).map(normalizePlan).forEach((p) => { map[p.id] = p; });
        setPlansById(map);
      })
      .catch(() => {});
  }, []);

  const handleCancel = async () => {
    if (!confirmSub) return;
    setCancelling(true);
    setCancelError(null);
    setCancelDetail(null);
    try {
      // candidateId ve referans kodu BE'de JWT'den çözülüyor
      await cancelSubscription();
      setCancelled(true);
      setConfirmSub(null);
      load();
    } catch (e) {
      setCancelError(e.code === 'NOT_IMPLEMENTED' || e.code === 'UNAUTHORIZED' ? 'pending' : 'failed');
      setCancelDetail(e.message || null);
    } finally {
      setCancelling(false);
    }
  };

  const subFor = (productKey) =>
    subs.find((s) => String(s.category || '').toLowerCase() === productKey.toLowerCase()) || null;

  // Bilinen ürünlere denk gelmeyen abonelikler de listelenir
  const knownKeys = PRODUCTS.map((p) => p.key.toLowerCase());
  const extraSubs = subs.filter(
    (s) => !knownKeys.includes(String(s.category || '').toLowerCase())
  );

  const renderCard = (product, sub, cardKey) => {
    const plan = sub ? plansById[sub.planId] : null;
    const key = statusKey(sub?.status);
    const status = STATUS[key] || null;
    const StatusIcon = status?.icon;
    const isCancelled = key === 'CANCELED' || key === 'CANCELLED' || key === 'PASSIVE';
    const accessEnds = formatDate(sub?.endDate) || formatDate(sub?.nextPaymentDate);
    const nextPayment = formatDate(sub?.nextPaymentDate);
    const Icon = product.icon;

    const buyButton = (label, variant) => (
      <button
        className={`subscription__btn subscription__btn--${variant}`}
        onClick={() => setShowPurchase(product)}
      >
        {variant === 'ghost' && <Plus size={15} />} {label}
      </button>
    );

    return (
      <div className={`subscription__card ${sub ? '' : 'subscription__card--empty'}`} key={cardKey}>
        <div className="subscription__card-head">
          <div
            className="subscription__card-icon"
            style={{ background: `${product.color}14`, color: product.color }}
          >
            <Icon size={20} />
          </div>
          <div className="subscription__card-title">
            <h2 className="subscription__product">{product.name}</h2>
            <p className="subscription__product-desc">{product.desc}</p>
            {product.disclaimer && (
              <p className="subscription__disclaimer">{product.disclaimer}</p>
            )}
          </div>
          {sub && plan && (
            <div className="subscription__price">
              {formatPrice(plan.price, plan.currency)}
              {plan.period && <span className="subscription__period">/{plan.period}</span>}
            </div>
          )}
        </div>

        {sub ? (
          <>
            <div className="subscription__meta">
              <span className="subscription__plan-badge">
                {plan?.name || sub.planName || 'Paket'}
              </span>
              {status && (
                <span className="subscription__status" style={{ color: status.color }}>
                  <StatusIcon size={14} /> {status.label}
                </span>
              )}
              {formatDate(sub.startDate) && (
                <span className="subscription__meta-item">
                  <Calendar size={13} /> Başlangıç: {formatDate(sub.startDate)}
                </span>
              )}
              {nextPayment && !isCancelled && (
                <span className="subscription__meta-item">
                  <RefreshCw size={13} /> Sonraki ödeme: {nextPayment}
                </span>
              )}
              {accessEnds && isCancelled && (
                <span className="subscription__meta-item">
                  <Calendar size={13} /> Erişim bitiş: {accessEnds}
                </span>
              )}
            </div>

            <div className="subscription__actions">
              {isCancelled ? buyButton('Yeniden Başlat', 'primary') : (
                <>
                  {buyButton('Paketi Değiştir', 'primary')}
                  <button
                    className="subscription__btn subscription__btn--danger"
                    onClick={() => setConfirmSub(sub)}
                  >
                    İptal Et
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="subscription__actions">
            {buyButton('Paketleri Gör', 'ghost')}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="subscription-page">
      <SEO title="Aboneliklerim" path="/panel/abonelik" noindex />
      <div className="subscription__container">
        <BackButton />

        <div className="subscription__header">
          <div className="subscription__header-icon">
            <CreditCard size={24} />
          </div>
          <h1 className="subscription__title">Aboneliklerim</h1>
          <p className="subscription__subtitle">
            Aktif paketlerini görüntüle, yeni paket ekle veya dilediğin zaman iptal et.
          </p>
        </div>

        {loading ? (
          <div className="subscription__loading">
            <Loader2 size={22} className="subscription__spinner" />
            <p>Abonelikler yükleniyor...</p>
          </div>
        ) : (
          <>
            {loadError && (
              <div className={`subscription__alert ${loadError === 'pending' ? 'subscription__alert--pending' : ''}`}>
                <AlertCircle size={16} />
                <span>
                  {loadError === 'pending'
                    ? 'Abonelik servisi hazırlanıyor. Aktif paketin varsa birazdan burada görünecek.'
                    : 'Abonelik bilgileri alınamadı.'}
                  {loadError === 'failed' && loadDetail && (
                    <span className="subscription__alert-detail">{loadDetail}</span>
                  )}
                </span>
                {loadError === 'failed' && (
                  <button className="subscription__retry" onClick={load}>
                    <RefreshCw size={13} /> Tekrar dene
                  </button>
                )}
              </div>
            )}

            {cancelled && (
              <div className="subscription__alert subscription__alert--ok">
                <CheckCircle2 size={16} />
                <span>Aboneliğin iptal edildi. Dönem sonuna kadar kullanmaya devam edebilirsin.</span>
              </div>
            )}

            <div className="subscription__list">
              {PRODUCTS.map((product) => renderCard(product, subFor(product.key), product.key))}

              {extraSubs.map((sub) => renderCard(
                {
                  key: sub.category || sub.id,
                  name: sub.category || 'Abonelik',
                  icon: CreditCard,
                  color: '#6B7280',
                  desc: 'Hesabına tanımlı abonelik.',
                  purchaseTitle: 'Paketini Seç',
                },
                sub,
                sub.id
              ))}
            </div>

            <p className="subscription__note">
              İptal, içinde bulunduğun ödeme döneminin sonunda geçerli olur; dönem sonuna kadar
              hizmetten yararlanmaya devam edersin. Ayrıntılar için{' '}
              <a href="/iptal-iade" target="_blank" rel="noopener noreferrer">İptal ve İade Koşulları</a>.
            </p>
          </>
        )}

        {/* İptal onayı */}
        {confirmSub && (
          <div
            className="subscription__overlay"
            onClick={(e) => e.target === e.currentTarget && !cancelling && setConfirmSub(null)}
          >
            <div className="subscription__confirm">
              <h3 className="subscription__confirm-title">Aboneliği iptal et</h3>
              <p className="subscription__confirm-text">
                <strong>{plansById[confirmSub.planId]?.name || confirmSub.planName || 'Paketin'}</strong>{' '}
                iptal edilecek. Kartından yeni çekim yapılmayacak
                {(formatDate(confirmSub.endDate) || formatDate(confirmSub.nextPaymentDate))
                  ? `, ${formatDate(confirmSub.endDate) || formatDate(confirmSub.nextPaymentDate)} tarihine kadar`
                  : ''}{' '}
                kullanmaya devam edebileceksin.
              </p>

              {cancelError && (
                <div className={`subscription__alert ${cancelError === 'pending' ? 'subscription__alert--pending' : ''}`}>
                  <AlertCircle size={16} />
                  <span>
                    {cancelError === 'pending'
                      ? 'Abonelik servisi hazırlanıyor. İptal için info@isbulsun.com adresine yazabilirsin.'
                      : 'İptal işlemi tamamlanamadı. Lütfen tekrar dene.'}
                    {cancelDetail && <span className="subscription__alert-detail">{cancelDetail}</span>}
                  </span>
                </div>
              )}

              <div className="subscription__confirm-actions">
                <button
                  className="subscription__btn subscription__btn--danger"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? (
                    <><Loader2 size={15} className="subscription__spinner" /> İptal ediliyor...</>
                  ) : (
                    'Evet, iptal et'
                  )}
                </button>
                <button
                  className="subscription__btn subscription__btn--ghost"
                  onClick={() => setConfirmSub(null)}
                  disabled={cancelling}
                >
                  Vazgeç
                </button>
              </div>
            </div>
          </div>
        )}

        <PurchaseModal
          isOpen={!!showPurchase}
          onClose={() => setShowPurchase(null)}
          category={showPurchase?.key || 'AutoApply'}
          title={showPurchase?.purchaseTitle || 'AI Asistan Lisansını Seç'}
          currentPlanId={subFor(showPurchase?.key || 'AutoApply')?.planId}
          onSuccess={load}
        />
      </div>
    </div>
  );
};

export default SubscriptionPage;
